import React from 'react'
import { useTranslation } from 'react-i18next'
import useTokens from 'src/common/apollo/hooks/useTokens'
import { OffersQueryVariables } from 'src/common/apollo/types/swarmx/graphql'
import Button from 'src/common/ui-kit/Button/Button'
import Checkbox from 'src/common/ui-kit/Checkbox/Checkbox'
import Icon from 'src/common/ui-kit/Icon/Icon'
import Option from 'src/common/ui-kit/Select/Option'
import Select from 'src/common/ui-kit/Select/Select'
import TokenIcon from 'src/common/ui-kit/TokenIcon/TokenIcon'
import {
  ExpandedOfferFillType,
  Filters,
  OfferFillTypeOptions,
  TimeRangeOptions,
} from '../filters'
import './styles.css'
import useManageFilter from '../hooks/useManageFilter'
import { activeTabAtom } from 'src/pages/DiscoverPage/discover-store'
import { useAtomValue } from 'jotai'
import uniqBy from 'lodash/uniqBy'
import { filterOptions } from '../utils'
import useCurrentAssetValues from '../hooks/useCurrentAssetValues'

export interface OffersFilterProps {
  onFilterChange: (filters: OffersQueryVariables['filter']) => void
}

export type AssetOptionKey = keyof Pick<
  Filters,
  'depositAssets' | 'withdrawalAssets'
>

const OffersFilterDesktop: React.FC<OffersFilterProps> = ({
  onFilterChange,
}) => {
  const { erc20s, nfts } = useTokens()
  const { t } = useTranslation('offersManagement', {
    keyPrefix: 'offersFilter',
  })

  const activeTab = useAtomValue(activeTabAtom)

  const {
    filters,
    updateOfferFillTypeFilter,
    updateTimeRangeFilter,
    resetFilters,
    swapAssetFilters,
  } = useManageFilter(onFilterChange, true)

  const {
    currentFilterHandlers,
    currentRenderedValue,
    currentAssetOptionKey,
    currentFilters,
  } = useCurrentAssetValues(onFilterChange)

  const filteredOfferFillTypeOptions = filterOptions(
    OfferFillTypeOptions,
    activeTab
  )

  const assets = [...erc20s, ...uniqBy(nfts, 'contractAddress')]

  const assetFilterOptions = assets.map(item => ({
    value: item.contractAddress,
    label: item.symbol,
  }))

  const getFillTypeValue = (values: ExpandedOfferFillType[]) => {
    return OfferFillTypeOptions.filter(item => values.includes(item.value))
      .map(item => item.label)
      .join(', ')
  }

  const getAssetOption = (
    value: string,
    label: string,
    key: AssetOptionKey
  ) => {
    return (
      <Option className='flex flex-row items-center' key={value} value={value}>
        <div className='flex items-center justify-between w-full'>
          <div className='flex items-center'>
            <TokenIcon
              token={assets.find(item => item.contractAddress === value)}
            />
            <div className='mx-2'>{label}</div>
          </div>
          <Checkbox
            className='ml-auto'
            checked={filters[key].toString().includes(value)}
          />
        </div>
      </Option>
    )
  }

  return (
    <div className='comp-offer-filter-desktop'>
      <div className='comp-offer-filter-item'>
        <span className='comp-offer-filter-title'>
          {t('withdrawalAssets.label')}
        </span>
        <div className='relative'>
          <Select<string>
            multiselect
            className='comp-offer-filter-select'
            value={currentFilters.sell}
            onChange={currentFilterHandlers.sell}
            placeholder={t('withdrawalAssets.placeholder')}
            renderedValue={currentRenderedValue.sell}
          >
            {assetFilterOptions.map(({ value, label }) =>
              getAssetOption(value, label, currentAssetOptionKey.sell)
            )}
          </Select>
          <Icon
            onClick={swapAssetFilters}
            className='comp-offer-swap-icon'
            name='swap-horiz'
            size={12}
          />
        </div>
      </div>

      <div className='comp-offer-filter-item'>
        <span className='comp-offer-filter-title'>
          {t('depositAssets.label')}
        </span>
        <Select<string>
          multiselect
          className='comp-offer-filter-select'
          value={currentFilters.buy}
          onChange={currentFilterHandlers.buy}
          placeholder={t('depositAssets.placeholder')}
          renderedValue={currentRenderedValue.buy}
        >
          {assetFilterOptions.map(({ value, label }) =>
            getAssetOption(value, label, currentAssetOptionKey.buy)
          )}
        </Select>
      </div>

      <div className='comp-offer-filter-item'>
        <span className='comp-offer-filter-title'>
          {t('offerStatus.label')}
        </span>
        <Select<ExpandedOfferFillType>
          multiselect
          className='comp-offer-filter-select'
          value={filters.offerStatuses}
          onChange={updateOfferFillTypeFilter}
          renderedValue={getFillTypeValue(filters.offerStatuses)}
          placeholder={t('offerStatus.placeholder')}
        >
          {filteredOfferFillTypeOptions.map(({ value, label }) => (
            <Option
              className='flex flex-row items-center'
              key={value}
              value={value}
            >
              <div>{label}</div>
              <Checkbox checked={filters.offerStatuses.includes(value)} />
            </Option>
          ))}
        </Select>
      </div>

      <div className='comp-offer-filter-item'>
        <span className='comp-offer-filter-title'>{t('timeRange.label')}</span>
        <Select<number>
          className='comp-offer-filter-select'
          value={filters.timeRange}
          onChange={updateTimeRangeFilter}
          placeholder={t('timeRange.placeholder')}
        >
          {TimeRangeOptions.map(({ value, label }) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </Select>
      </div>

      <Button
        onClick={resetFilters}
        className='comp-offer-filter-reset'
        variant='text'
      >
        <Icon name='filter-remove' width='18px' height='18px' />
      </Button>
    </div>
  )
}

export default OffersFilterDesktop
